import { Component } from '@verndale/core';
import Swiper from 'swiper/js/swiper';

import breakpoints from '../utils/breakpoints';
import { disableFocusOnHiddenSlides } from '../utils/slider';
import { isIE } from '../utils/browsers';

function getVideoId(index) {
  return `video-${index}`;
}

const IS_PLAYING = 'playing';

class GallerySlider extends Component {
  setupDefaults() {
    this.dom = {
      $heading: this.el._$('h2'),
      $swiperContainer: this.el._$('.swiper-container'),
      $mediaWrappers: this.el._$$('.gallery-figure__media-wrapper'),
      $figureCaptions: this.el._$$('.gallery-figure__caption'),
      $activeCaption: this.el._$('.gallery-slider__caption'),
      $slides: this.el._$$('.swiper-slide'),
      $desktopPagination: this.el._$('.swiper-pagination--desktop')
    };

    this.swiper = null;
    this.addVideoPlayers();
    this.initSlider();
    this.updatePagination();
    this.updateFocusableElements();
  }

  addListeners() {
    this.dom.$mediaWrappers._on('click', ::this.playVideo);
    if (this.swiper) {
      this.swiper.on('slideChangeTransitionStart', ::this.hideCaption);
      this.swiper.on('slideChangeTransitionEnd', () => {
        this.handleTransitionEnd();
        if (!isIE) {
          this.handleVideoPause();
        }
      });
    }
  }

  handleVideoPause() {
    const { $slides } = this.dom;

    $slides.forEach(($slide, index) => {
      const isActive = $slide.classList.contains('swiper-slide-active');

      if (!isActive && this.dom[getVideoId(index)]?.getState() === IS_PLAYING) {
        this.dom[getVideoId(index)]?.pause();
      }
    });
  }

  handleTransitionEnd() {
    this.showCaption();
    this.updatePagination();
  }

  initSlider() {
    const { $swiperContainer, $slides, $heading } = this.dom;

    this.swiper = new Swiper($swiperContainer, {
      a11y: {
        enabled: true,
        nextSlideMessage: `Go to ${
          $heading
            ? `${$heading.textContent.replace(/(\r\n|\n|\r)/gm, '').trim()}`
            : ''
        } next slide`,
        prevSlideMessage: `Go to ${
          $heading
            ? `${$heading.textContent.replace(/(\r\n|\n|\r)/gm, '').trim()}`
            : ''
        } previous slide`
      },
      slideActiveClass: 'swiper-slide-active swiper-slide-visible',
      watchSlidesProgress: true,
      watchOverflow: true,
      spaceBetween: 5,
      keyboard: {
        enabled: true
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      },
      pagination: {
        dynamicBullets: true,
        clickable: true,
        el: '.swiper-pagination',
        bulletClass: 'swiper-bullet',
        bulletActiveClass: 'swiper-bullet--active',
        modifierClass: 'swiper-bullet--'
      },
      breakpoints: {
        [breakpoints.tablet]: {
          spaceBetween: 0,
          watchSlidesProgress: true,
          slideActiveClass: 'swiper-slide-active swiper-slide-visible'
        }
      },
      on: {
        init() {
          $swiperContainer.classList.remove('loading');
          $swiperContainer.classList.add('loaded');
        }
      }
    });
    this.showCaption();
  }

  playVideo(event) {
    const { $mediaWrappers } = this.dom;
    const $wrapper = event.target.closest('.gallery-figure__media-wrapper');
    const index = $mediaWrappers.findIndex(($mW) => $mW === $wrapper);
    const $button = $wrapper._$('.gallery-figure__play-button');
    const $image = $wrapper._$('.gallery-figure__image');
    const $video = $wrapper._$('.gallery-figure__video');

    if (!$button) return;

    if (isIE) {
      const $iframe = $wrapper._$('iframe');

      $iframe.src = `${$iframe.src}?autoplay=1`;
      $iframe.removeAttribute('tabindex');
    } else {
      this.dom[getVideoId(index)]?.play();
    }
    $video.removeAttribute('style');
    $wrapper.removeChild($image);
    $wrapper.removeChild($button);
  }

  addVideoPlayers() {
    if (isIE) {
      this.dom.$mediaWrappers.forEach(($mediaWrapper, index) => {
        const { playButtonLabel, videoId, videoTitle } = $mediaWrapper.dataset;

        if (!videoId) return;

        $mediaWrapper.insertAdjacentHTML(
          'afterbegin',
          this.getIeIframe(videoId, videoTitle)
        );
        $mediaWrapper.insertAdjacentHTML(
          'beforeend',
          this.getPlayButton(playButtonLabel)
        );
      });
    } else {
      import('yt-player').then((module) => {
        const YTPlayer = module.default;
        this.dom.$mediaWrappers.forEach(($mediaWrapper, index) => {
          const { playButtonLabel, videoId } = $mediaWrapper.dataset;

          if (!videoId) return;

          $mediaWrapper.insertAdjacentHTML('afterbegin', this.getIframe(index));
          $mediaWrapper.insertAdjacentHTML(
            'beforeend',
            this.getPlayButton(playButtonLabel)
          );

          const element = this.el._$(`.gallery-figure__video-${index}`);

          this.dom[getVideoId(index)] = new YTPlayer(element, {
            related: false,
            modestBranding: true,
            host: '//youtube.com'
          });
          this.dom[getVideoId(index)].load(videoId);
        });
      });
    }
  }

  getIeIframe(videoId, videoTitle) {
    return `
      <div class="gallery-figure__video" style="z-index: -1;">
        <iframe
          loading="lazy"
          src="https://www.youtube.com/embed/${videoId}"
          frameborder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen="allowfullscreen"
          msallowfullscreen="msallowfullscreen"
          webkitallowfullscreen="webkitallowfullscreen"
          title="${videoTitle}"
          tabindex="-1"
        ></iframe>
      </div>
    `;
  }

  getIframe(index) {
    return `
      <div class="gallery-figure__video" style="z-index: -1;">
        <div class="gallery-figure__video-${index}" tabindex="-1"></div>
      </div>
    `;
  }

  getPlayButton(playButtonLabel) {
    return `
      <button class="gallery-figure__play-button" aria-label="${playButtonLabel}">
        <svg aria-hidden="true">
          <use xlink:href="#play-outline" />
        </svg>
      </button>
    `;
  }

  hideCaption() {
    this.dom.$activeCaption.classList.remove('visible');
  }

  showCaption() {
    const { $activeCaption } = this.dom;
    const newCaptionNode = this.el._$(
      '.swiper-slide-active .gallery-figure__caption'
    );

    if (newCaptionNode) {
      $activeCaption.innerHTML = newCaptionNode.innerHTML;
    } else {
      $activeCaption.innerHTML = '';
    }
    $activeCaption.classList.add('visible');
  }

  updatePagination() {
    const { $desktopPagination, $slides } = this.dom;

    const currentSlideIndex = $slides.findIndex(($slide) => {
      return Array.from($slide.classList).includes('swiper-slide-active');
    });

    $desktopPagination.innerHTML = `${currentSlideIndex + 1} of ${
      $slides.length
    }`;
  }

  updateFocusableElements() {
    const { $slides } = this.dom;
    if (!this.swiper) return;
    this.swiper.on('slideChangeTransitionStart', () =>
      disableFocusOnHiddenSlides($slides)
    );

    disableFocusOnHiddenSlides($slides);
  }
}

export default GallerySlider;
